<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import ArgonCalendar from "@/components/ArgonCalendar.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import { dateUtils } from "@/utils";
import { stopStore } from "@/_store/stopStore";
import { errorUtils } from "@/utils";

const modalRetryDelivery = ref(null);
const props = defineProps({
  modalId: { type: String, required: true },
  stopIds: {
    type: Array,
    default() {
      return [];
    },
  },
});

const emit = defineEmits(["close", "success"]);

const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const stopsStore = stopStore();

const isLoading = ref(false);
const isSaving = ref(false);
const snackbar = ref(null);
const form = ref({ date: "" });

const resolvedStopIds = computed(() => props.stopIds.filter(Boolean));
const todayDate = computed(() => dateUtils.formatDate(new Date(), "YYYY-MM-DD"));

const rules = {
  date: {
    required: helpers.withMessage("La fecha es requerida.", required),
    validDateFormat: helpers.withMessage("La fecha no es válida.", (value) =>
      /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").slice(0, 10))
    ),
    isNotPastDate: helpers.withMessage("La fecha debe ser igual o posterior a hoy.", (value) => {
      const selectedDate = String(value || "").slice(0, 10);
      if (!selectedDate) return true;
      return selectedDate >= todayDate.value;
    }),
  },
};

const v$ = useVuelidate(rules, form);

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo guardar el nuevo intento de reparto",
    type: "danger",
    color: "white",
  };
}

function loadInitialData() {
  if (resolvedStopIds.value.length === 0) {
    showError("Debe seleccionar al menos una parada para crear un nuevo intento de reparto.");
    return;
  }

  form.value.date = todayDate.value;
  v$.value.$reset();
  isLoading.value = false;
}

async function validateForm() {
  await v$.value.$validate();
  return v$.value.$errors;
}

async function saveRetryDelivery() {
  if (isSaving.value || isLoading.value) return;

  const validationErrors = await validateForm();
  if (validationErrors.length > 0) {
    const message = validationErrors.map((value) => `<li>${value.$message}</li>`).join("");
    if (message) {
      snackbar.value = {
        title: "Error",
        message: `<ul>${message}</ul>`,
        type: "danger",
        color: "white",
      };
    }
    return;
  }

  const stopIds = resolvedStopIds.value;
  if (stopIds.length === 0) {
    showError("Debe seleccionar al menos una parada para crear un nuevo intento de reparto.");
    return;
  }

  isSaving.value = true;
  closeSnackbar();

  stopsStore
    .retryDelivery(stopIds, {
      date: String(form.value.date || "").slice(0, 10),
    })
    .catch((value) => {
      const message = stopIds.length === 1
        ? errorUtils.getErrorMessageHtml(value, "No se pudo crear el nuevo intento de reparto.")
        : errorUtils.getErrorMessageHtml(value, "No se pudo crear el nuevo intento de reparto para algunas paradas.");
      showError(message);
    });
}

function handleHide() {
  form.value.date = "";
  v$.value.$reset();
  emit("close");
}

watch(
  () => stopsStore.stopDateUpdated,
  (value) => {
    if (!isSaving.value || !value) return;
    isSaving.value = false;
    modalRetryDelivery.value.closeModal();
    emit("success");
  }
);

watch(
  () => stopsStore.error,
  (value) => {
    if (!value) return;
    if (!isLoading.value && !isSaving.value) return;
    const message =
      resolvedStopIds.value.length === 1
        ? errorUtils.getErrorMessageHtml(value, "No se pudo crear el nuevo intento de reparto.")
        : errorUtils.getErrorMessageHtml(value, "No se pudo crear el nuevo intento de reparto para algunas paradas.");
    showError(message);
    isLoading.value = false;
    isSaving.value = false;
  }
);

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById(props.modalId), {
    keyboard: true,
    backdrop: true,
  });
  modal.show();
  loadInitialData();
});
</script>

<template>
  <modal-base
    :id="modalId"
    ref="modalRetryDelivery"
    width="30rem"
    @hide="handleHide"
  >
    <template #header>
      <h6 class="mb-0">Nuevo intento de reparto</h6>
    </template>

    <template #body>
      <alert-message title="Aviso" type="info" class="mb-3">
        Indica la fecha en la que quieres reintentar la entrega. El sistema volverá a procesar la parada o paradas seleccionadas.
      </alert-message>

      <div class="row align-items-center mb-0">
        <div class="col">
          <argon-calendar
            id="stop-retry-delivery-date"
            v-model="form.date"
            type="date"
            :error="v$.date.$error"
            :disabled="isLoading || isSaving"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="isSaving ? 'Guardando...' : 'Guardar'"
        :loading="isSaving"
        :disable-button="isLoading || isSaving"
        :error="v$.date.$error"
        @click="saveRetryDelivery"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
